import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import './PriceStickerPrinter.css';

/**
 * Price Sticker Printer — Candlefish (Charleston) retail price stickers.
 *
 * Search the Candlefish Shopify store for an item, set how many stickers you need,
 * and print onto 1.5" x 1" OnlineLabels OL1000 / OL1000WX sheets (5 across x 10 down,
 * 50 per sheet). The barcode encodes the Shopify variant `barcode` value (the UPC/EAN
 * the registers scan), fetched live from Shopify. Items with no barcode in Shopify are
 * kept in the list but skipped at print time.
 *
 * Print: use the browser print dialog. Set paper to Letter, Margins = None/Default,
 * Scale = 100% (Actual size), and turn Headers/Footers off, then "Save as PDF" or
 * send to the label printer.
 */

const SHEET_CAPACITY = 50; // OL1000: 5 columns x 10 rows
const MAX_QTY = 300;

// Pick the barcode symbology Shopify/registers expect from the data shape.
function detectFormat(value) {
  const v = String(value || '').trim();
  if (/^\d{12}$/.test(v)) return 'UPC';     // UPC-A
  if (/^\d{13}$/.test(v)) return 'EAN13';
  if (/^\d{8}$/.test(v)) return 'EAN8';
  return 'CODE128';
}

function StickerBarcode({ value }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !value) return;
    const primary = detectFormat(value);
    const attempt = (format) => {
      JsBarcode(ref.current, String(value), {
        format,
        width: 1.3,
        height: 34,
        displayValue: true,
        fontSize: 9,
        textMargin: 1,
        margin: 0,
      });
    };
    try {
      attempt(primary);
    } catch (e) {
      // Bad check digit / wrong length for a numeric symbology — fall back.
      try {
        attempt('CODE128');
      } catch (e2) {
        console.error('Barcode render failed for', value, e2);
      }
    }
  }, [value]);

  return <svg ref={ref} className="ps-barcode-svg" />;
}

function money(price) {
  const n = parseFloat(price);
  if (!isFinite(n)) return price || '';
  return '$' + n.toFixed(2);
}

export default function PriceStickerPrinter() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState([]); // { variantId, productTitle, variantTitle, sku, barcode, hasBarcode, price, qty }
  const [startPos, setStartPos] = useState(1);
  const debounceRef = useRef(null);

  // "order" mode: pull every product line on a NetSuite SO / transfer order.
  const [mode, setMode] = useState('item'); // 'item' | 'order'
  const [orderNumber, setOrderNumber] = useState('');
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderMeta, setOrderMeta] = useState(null);
  const [orderSkipped, setOrderSkipped] = useState([]);

  const loadOrder = useCallback(async () => {
    const number = orderNumber.trim();
    if (number.length < 2) return;
    setOrderLoading(true);
    setOrderError(null);
    try {
      const res = await axios.get('/api/price-stickers/order', { params: { number } });
      const rows = res.data?.rows || [];
      setQueue(
        rows.map((r) => ({
          variantId: r.variantId,
          productTitle: r.productTitle,
          variantTitle: r.variantTitle || '',
          sku: r.sku,
          barcode: r.barcode,
          hasBarcode: r.hasBarcode,
          price: r.price,
          qty: Math.max(1, Math.min(MAX_QTY, r.qty || 1)),
          fromOrder: {
            unitsPerCase: r.unitsPerCase,
            caseCount: r.caseCount,
            singleCount: r.singleCount,
            orderSkus: r.orderSkus || [],
            unresolvedCasePack: r.unresolvedCasePack,
          },
        }))
      );
      setOrderMeta({ ...res.data.order, rowCount: rows.length });
      setOrderSkipped(res.data?.skipped || []);
      if (rows.length === 0) {
        setOrderError('That order has no Rewined/Candlefish product lines to sticker.');
      }
    } catch (e) {
      setOrderMeta(null);
      setOrderSkipped([]);
      setOrderError(e.response?.data?.error || 'Order lookup failed. Try again.');
    } finally {
      setOrderLoading(false);
    }
  }, [orderNumber]);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/price-stickers/search', { params: { q } });
      setResults(res.data?.results || []);
    } catch (e) {
      setResults([]);
      setError(e.response?.data?.error || 'Search failed. Try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const onQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const addToQueue = (item) => {
    setQueue((prev) => {
      const existing = prev.find((r) => r.variantId === item.variantId);
      if (existing) {
        return prev.map((r) =>
          r.variantId === item.variantId
            ? { ...r, qty: Math.min(MAX_QTY, r.qty + 1) }
            : r
        );
      }
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const setQty = (variantId, qty) => {
    const n = Math.max(1, Math.min(MAX_QTY, parseInt(qty, 10) || 1));
    setQueue((prev) => prev.map((r) => (r.variantId === variantId ? { ...r, qty: n } : r)));
  };

  const removeRow = (variantId) => {
    setQueue((prev) => prev.filter((r) => r.variantId !== variantId));
  };

  const clearQueue = () => setQueue([]);

  const printable = queue.filter((r) => r.hasBarcode);
  const skipped = queue.filter((r) => !r.hasBarcode);
  const totalStickers = printable.reduce((sum, r) => sum + r.qty, 0);

  // Expand the queue into one entry per physical sticker.
  const stickerList = [];
  printable.forEach((r) => {
    for (let i = 0; i < r.qty; i += 1) {
      stickerList.push({ key: r.variantId + '-' + i, sku: r.sku, price: r.price, barcode: r.barcode });
    }
  });

  // Lay out onto sheets, offsetting the first sheet by (startPos - 1) blank cells
  // so a partially used sheet can be reused.
  const offset = Math.max(0, Math.min(SHEET_CAPACITY - 1, startPos - 1));
  const cells = [];
  for (let i = 0; i < offset; i += 1) cells.push({ blank: true, key: 'blank-' + i });
  cells.push(...stickerList.map((s) => ({ ...s, blank: false })));
  const sheets = [];
  for (let i = 0; i < cells.length; i += SHEET_CAPACITY) {
    sheets.push(cells.slice(i, i + SHEET_CAPACITY));
  }

  const handlePrint = () => {
    if (totalStickers === 0) return;
    window.print();
  };

  return (
    <div className="ps-app">
      <header className="ps-header">
        <div>
          <h1>Price Sticker Printer</h1>
          <p className="ps-sub">Candlefish &middot; Charleston store</p>
        </div>
        <div className="ps-paper-note">
          OnlineLabels OL1000 / OL1000WX &mdash; 1.5&quot; &times; 1&quot;, 50 per sheet
        </div>
      </header>

      <div className="ps-body">
        <section className="ps-search-col">
          <div className="ps-mode-toggle" role="tablist">
            <button
              type="button"
              className={`ps-mode-btn ${mode === 'item' ? 'active' : ''}`}
              onClick={() => setMode('item')}
            >
              Search item
            </button>
            <button
              type="button"
              className={`ps-mode-btn ${mode === 'order' ? 'active' : ''}`}
              onClick={() => setMode('order')}
            >
              Whole order
            </button>
          </div>

          {mode === 'item' ? (
            <>
              <label className="ps-field-label" htmlFor="ps-search">Find an item</label>
              <input
                id="ps-search"
                className="ps-search-input"
                type="text"
                value={query}
                onChange={onQueryChange}
                placeholder="Search by product name, SKU, or barcode"
                autoFocus
              />
              <div className="ps-hint">9oz jars &amp; 2.5oz tins are excluded &mdash; Charleston doesn't stock these sizes.</div>
              {loading && <div className="ps-hint">Searching Shopify&hellip;</div>}
              {error && <div className="ps-error">{error}</div>}

              {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
                <div className="ps-hint">No matches in the Candlefish Shopify catalog.</div>
              )}

              <div className="ps-results">
                {results.map((item) => (
                  <button
                    type="button"
                    key={item.variantId}
                    className="ps-result"
                    onClick={() => addToQueue(item)}
                  >
                    <div className="ps-result-main">
                      <span className="ps-result-title">{item.productTitle}</span>
                      {item.variantTitle && (
                        <span className="ps-result-variant">{item.variantTitle}</span>
                      )}
                    </div>
                    <div className="ps-result-meta">
                      <span className="ps-result-sku">{item.sku || 'no SKU'}</span>
                      <span className="ps-result-price">{money(item.price)}</span>
                      {item.hasBarcode ? (
                        <span className="ps-result-barcode">{item.barcode}</span>
                      ) : (
                        <span className="ps-badge-warn">no barcode in Shopify</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <label className="ps-field-label" htmlFor="ps-order">NetSuite order number</label>
              <div className="ps-order-row">
                <input
                  id="ps-order"
                  className="ps-search-input"
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadOrder()}
                  placeholder="Sales order or transfer order #"
                  autoFocus
                />
                <button
                  type="button"
                  className="ps-load-btn"
                  onClick={loadOrder}
                  disabled={orderLoading || orderNumber.trim().length < 2}
                >
                  {orderLoading ? 'Loading…' : 'Load'}
                </button>
              </div>
              <div className="ps-hint">
                Pulls every product line, converts cases to singles, and multiplies by
                units&#8209;per&#8209;case. Loading an order replaces the list.
              </div>
              {orderError && <div className="ps-error">{orderError}</div>}

              {orderMeta && (
                <div className="ps-order-meta">
                  <div><strong>{orderMeta.type || 'Order'} {orderMeta.number}</strong>
                    {orderMeta.date ? ` · ${String(orderMeta.date).slice(0, 10)}` : ''}</div>
                  <div>{orderMeta.rowCount} product{orderMeta.rowCount === 1 ? '' : 's'} loaded</div>
                </div>
              )}

              {orderSkipped.length > 0 && (
                <div className="ps-hint">
                  Skipped {orderSkipped.length} line{orderSkipped.length === 1 ? '' : 's'} (non&#8209;product,
                  or 9oz/2.5oz sizes Charleston doesn't stock):{' '}
                  {orderSkipped.slice(0, 6).map((s) => s.sku).join(', ')}
                  {orderSkipped.length > 6 ? '…' : ''}
                </div>
              )}
            </>
          )}
        </section>

        <section className="ps-queue-col">
          <div className="ps-queue-head">
            <h2>To print{totalStickers > 0 ? ` (${totalStickers} sticker${totalStickers === 1 ? '' : 's'})` : ''}</h2>
            {queue.length > 0 && (
              <button type="button" className="ps-link-btn" onClick={clearQueue}>Clear</button>
            )}
          </div>

          {queue.length === 0 && (
            <div className="ps-empty">
              {mode === 'order'
                ? 'Load an order number to fill this list.'
                : 'Search for an item and click it to add it here.'}
            </div>
          )}

          {queue.map((r) => (
            <div className={`ps-row ${r.hasBarcode ? '' : 'ps-row-skip'}`} key={r.variantId}>
              <div className="ps-row-info">
                <div className="ps-row-title">{r.productTitle}{r.variantTitle ? ` — ${r.variantTitle}` : ''}</div>
                <div className="ps-row-meta">
                  <span>{r.sku || 'no SKU'}</span>
                  <span>{money(r.price)}</span>
                  {r.hasBarcode
                    ? <span className="ps-row-barcode">{r.barcode}</span>
                    : <span className="ps-badge-warn">no barcode — will not print</span>}
                </div>
                {r.fromOrder && (r.fromOrder.caseCount > 0 || r.fromOrder.unitsPerCase > 1) && (
                  <div className="ps-row-sub">
                    {r.fromOrder.caseCount > 0 && (
                      <>{r.fromOrder.caseCount} case{r.fromOrder.caseCount === 1 ? '' : 's'} &times; {r.fromOrder.unitsPerCase}/case</>
                    )}
                    {r.fromOrder.singleCount > 0 && (
                      <> {r.fromOrder.caseCount > 0 ? '+ ' : ''}{r.fromOrder.singleCount} each</>
                    )}
                    {r.fromOrder.unresolvedCasePack && (
                      <span className="ps-badge-warn"> · case pack unknown, assumed 1 — check qty</span>
                    )}
                  </div>
                )}
              </div>
              <input
                type="number"
                className="ps-qty-input"
                min="1"
                max={MAX_QTY}
                value={r.qty}
                onChange={(e) => setQty(r.variantId, e.target.value)}
                disabled={!r.hasBarcode}
                aria-label="Sticker quantity"
              />
              <button type="button" className="ps-remove-btn" onClick={() => removeRow(r.variantId)} aria-label="Remove">
                &times;
              </button>
            </div>
          ))}

          {skipped.length > 0 && (
            <div className="ps-warn-banner">
              {skipped.length} item{skipped.length === 1 ? '' : 's'} without a Shopify barcode will be skipped.
            </div>
          )}

          <div className="ps-print-controls">
            <label className="ps-startpos">
              Start at label position
              <input
                type="number"
                min="1"
                max={SHEET_CAPACITY}
                value={startPos}
                onChange={(e) =>
                  setStartPos(Math.max(1, Math.min(SHEET_CAPACITY, parseInt(e.target.value, 10) || 1)))
                }
              />
              <span className="ps-startpos-hint">(1–50, counts left→right, top→bottom — reuse a partial sheet)</span>
            </label>

            <button
              type="button"
              className="ps-print-btn"
              onClick={handlePrint}
              disabled={totalStickers === 0}
            >
              Print {totalStickers > 0 ? `${totalStickers} sticker${totalStickers === 1 ? '' : 's'}` : ''}
            </button>
          </div>

          <div className="ps-print-tip">
            In the print dialog: Paper = <strong>Letter</strong>, Margins = <strong>None</strong>,
            Scale = <strong>100% / Actual size</strong>, Headers &amp; footers <strong>off</strong>.
            Then print to the label printer or <strong>Save as PDF</strong>.
          </div>

          {sheets.length > 0 && (
            <div className="ps-preview-wrap">
              <div className="ps-preview-label">Preview — sheet 1 of {sheets.length}</div>
              <div className="ps-preview-scale">
                <StickerSheet cells={sheets[0]} previewGrid />
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Print-only output (`print-only` hooks the app-wide print stylesheet) */}
      <div className="ps-print-only print-only">
        {sheets.map((sheetCells, idx) => (
          <StickerSheet key={idx} cells={sheetCells} pageBreak={idx < sheets.length - 1} />
        ))}
      </div>
    </div>
  );
}

function StickerSheet({ cells, pageBreak, previewGrid }) {
  // Always render a full 50-cell grid so partial sheets still align to the die-cut.
  const filled = cells.slice(0, SHEET_CAPACITY);
  while (filled.length < SHEET_CAPACITY) {
    filled.push({ blank: true, key: 'pad-' + filled.length });
  }
  return (
    <div className={`ps-sheet ${pageBreak ? 'ps-sheet-break' : ''} ${previewGrid ? 'ps-sheet-gridlines' : ''}`}>
      {filled.map((c) => (
        <div className="ps-sticker" key={c.key}>
          {!c.blank && (
            <>
              <div className="ps-sticker-price">{money(c.price)}</div>
              <div className="ps-sticker-barcode">
                <StickerBarcode value={c.barcode} />
              </div>
              <div className="ps-sticker-sku">{c.sku}</div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
